import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useFamilyFeatures } from '@/hooks/useFamilyFeatures'
import { setDashboardVariant } from '@/utils/dashboardVariant'
import { usePermissions } from '@/hooks/usePermissions'
import { useNavigate } from 'react-router-dom'
import { PriorityBar } from './dashboard/PriorityBar'
import { DashboardTiles } from './dashboard/DashboardTiles'
import { apiClient } from '@/utils/api'
import { LayoutGrid, Grid, Star, Flame, ChevronRight, Settings as SettingsIcon } from 'lucide-react'
import { AccountMenu } from './AccountMenu'
import { getTodayDateString, getTomorrowDateString, extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import { formatTimeString } from '@/utils/dateUtils'

interface DashboardStats {
  activeChores: number
  upcomingEvents: number
  activeKids: number
  pendingRewards: number
  needsVerification: number
  overdue: number
  kidRequests: number
  pendingAvailability: number
  todayEvents: number
  completedToday: number
  weeklyCompletionRate: number
  nextEvent?: {
    title: string
    time: string
  }
  topKid?: {
    name: string
    points: number
  }
  totalFamilyPoints: number
  tonightsDinner?: string
}

export const ParentDashboardV2: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { features, isFeatureEnabled } = useFamilyFeatures()
  const { hasPermission } = usePermissions()

  // Build list of tiles to hide based on permissions. Settings is no longer
  // a tile — it moved to a cog in the header to keep the 4×2 grid clean.
  const hiddenTiles: string[] = []
  if (!hasPermission('manage_rewards')) hiddenTiles.push('rewards')

  // Classic/Tiles switch is a per-family feature toggle (family_feature_toggles.legacy_view)
  const legacyViewEnabled = isFeatureEnabled('legacy_view')

  const [stats, setStats] = useState<DashboardStats>({
    activeChores: 0,
    upcomingEvents: 0,
    activeKids: 0,
    pendingRewards: 0,
    needsVerification: 0,
    overdue: 0,
    kidRequests: 0,
    pendingAvailability: 0,
    todayEvents: 0,
    completedToday: 0,
    weeklyCompletionRate: 0,
    totalFamilyPoints: 0
  })
  const [kidsOverview, setKidsOverview] = useState<Array<{
    id: string
    display_name: string
    color_theme: string
    points: number
    level: number
    streak_days: number
    available_points: number
  }>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  const fetchDashboardStats = async () => {
    try {
      setLoading(true)

      // Get today's and tomorrow's dates in family timezone for accurate date comparisons
      const todayStr = await getTodayDateString()
      const tomorrowStr = await getTomorrowDateString()

      // Calculate date 30 days from now for unified calendar query
      const thirtyDaysLater = new Date()
      thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30)
      const endDateStr = extractDateInTimezoneSync(thirtyDaysLater.toISOString())

      const [assignmentsRes, childrenRes, eventsRes, rewardsRes, scheduleRes, mealsRes, kidRequestsRes, availabilityRes] = await Promise.all([
        apiClient.get('/api/assignments?date_filter=all'),
        apiClient.get('/api/parent/children'),
        apiClient.get(`/api/calendar/unified?start_date=${todayStr}&end_date=${endDateStr}`).catch(() => null),
        apiClient.get('/api/rewards/redemptions').catch(() => null),
        apiClient.getScheduleRange(todayStr, tomorrowStr).catch(() => null),
        apiClient.get(`/api/meals/week-plan?start=${todayStr}&end=${todayStr}`).catch(() => null),
        apiClient.get('/api/events/kid-requests').catch(() => null),
        apiClient.get('/api/availability/overrides').catch(() => null)
      ])

      // Handle assignments response (apiClient.get already returns data, not full response)
      let completedToday = 0
      let weeklyCompletionRate = 0
      if (assignmentsRes) {
        const assignments = assignmentsRes.assignments || assignmentsRes.data || []
        const needsVerification = assignments.filter((a: any) => a.status === 'completed' && !a.verified_at).length
        // Use family timezone for overdue detection
        // Only count assignments that are actively assigned but past due
        // Exclude completed, verified, and pending_verification statuses
        const overdue = assignments.filter((a: any) => {
          const activeStatuses = ['pending', 'assigned', 'in_progress', 'open']
          if (!activeStatuses.includes(a.status)) return false
          if (a.verified_at) return false
          const dueDateStr = extractDateInTimezoneSync(a.due_date)
          return dueDateStr < todayStr
        }).length
        // Count all non-completed assignments as "active"
        const activeChores = assignments.filter((a: any) =>
          a.status !== 'completed' && a.status !== 'verified'
        ).length

        // Calculate completed today using family timezone
        completedToday = assignments.filter((a: any) =>
          a.status === 'completed' && a.completed_at && a.completed_at.startsWith(todayStr)
        ).length

        // Calculate weekly completion rate using timezone-aware dates
        const weekAgoDate = new Date()
        weekAgoDate.setDate(weekAgoDate.getDate() - 7)
        const weekAgoStr = extractDateInTimezoneSync(weekAgoDate.toISOString())
        const weeklyAssignments = assignments.filter((a: any) => {
          const dueDateStr = extractDateInTimezoneSync(a.due_date)
          // Include assignments due in the last 7 days up to today
          return dueDateStr >= weekAgoStr && dueDateStr <= todayStr
        })
        const weeklyCompleted = weeklyAssignments.filter((a: any) => a.status === 'completed' || a.status === 'verified').length
        weeklyCompletionRate = weeklyAssignments.length > 0
          ? Math.round((weeklyCompleted / weeklyAssignments.length) * 100)
          : 0

        setStats(prev => ({
          ...prev,
          activeChores,
          needsVerification,
          overdue,
          completedToday,
          weeklyCompletionRate
        }))
      }

      // Handle children response
      let topKid
      let totalFamilyPoints = 0
      if (childrenRes) {
        const children = childrenRes.children || childrenRes.data || []

        // Fetch monthly leaderboard to get this month's points
        try {
          const monthlyLeaderboard = await apiClient.getLeaderboard('month', 0)
          if (monthlyLeaderboard && monthlyLeaderboard.length > 0) {
            // Calculate total monthly family points
            totalFamilyPoints = monthlyLeaderboard.reduce((sum: number, entry: any) => sum + (entry.points || 0), 0)

            // Top kid is already ranked #1 in leaderboard
            topKid = {
              name: monthlyLeaderboard[0].display_name,
              points: monthlyLeaderboard[0].points || 0
            }

            // Store full leaderboard for kids overview
            setKidsOverview(monthlyLeaderboard.map((entry: any) => ({
              id: entry.id,
              display_name: entry.display_name,
              color_theme: entry.color_theme || '#6366f1',
              points: entry.points || 0,
              level: entry.level || 1,
              streak_days: entry.streak_days || 0,
              available_points: entry.available_points ?? entry.total_points ?? 0
            })))
          }
        } catch (error) {
          // Fallback to total points if leaderboard fails
          totalFamilyPoints = children.reduce((sum: number, child: any) => sum + (child.total_points || 0), 0)
          if (children.length > 0) {
            const sorted = [...children].sort((a: any, b: any) => (b.total_points || 0) - (a.total_points || 0))
            topKid = {
              name: sorted[0].display_name || sorted[0].username,
              points: sorted[0].total_points || 0
            }
          }
        }

        setStats(prev => ({
          ...prev,
          activeKids: children.length,
          topKid,
          totalFamilyPoints
        }))
      }

      // Handle events response - unified calendar returns {events: [...], total: n}
      let nextEvent
      if (eventsRes) {
        // Unified calendar response has events array
        const allEvents = eventsRes.events || []
        // Filter out custody events - we only want family, school, district events
        const events = allEvents.filter((e: any) => e.type !== 'custody')
        // Use family timezone for date comparisons - unified API uses 'date' field
        const todayEvents = events.filter((e: any) => e.date === todayStr).length
        // Count events from today onwards (compare date strings, not datetime objects)
        const upcomingEvents = events.filter((e: any) => e.date >= todayStr).length

        // Find next upcoming event
        const futureEvents = events
          .filter((e: any) => e.date >= todayStr)
          .sort((a: any, b: any) => a.date.localeCompare(b.date))

        if (futureEvents.length > 0) {
          const next = futureEvents[0]
          // Format time from start_time if available, otherwise show date
          const timeDisplay = next.start_time
            ? formatTimeString(next.start_time)
            : (next.all_day ? 'All day' : '')
          nextEvent = {
            title: next.title || 'Upcoming Event',
            time: timeDisplay
          }
        }

        setStats(prev => ({
          ...prev,
          todayEvents,
          upcomingEvents,
          nextEvent
        }))
      }

      // Handle rewards redemptions
      if (rewardsRes) {
        const redemptions = rewardsRes.redemptions || rewardsRes.data || []
        const pendingRewards = redemptions.filter((r: any) =>
          r.status === 'pending' || r.status === 'requested'
        ).length

        setStats(prev => ({
          ...prev,
          pendingRewards
        }))
      }

      // Handle kid requests
      if (kidRequestsRes) {
        const requests = Array.isArray(kidRequestsRes) ? kidRequestsRes : (kidRequestsRes.requests || [])
        const pendingRequests = requests.filter((r: any) => r.status === 'pending').length
        setStats(prev => ({
          ...prev,
          kidRequests: pendingRequests
        }))
      }

      // Handle pending availability overrides
      if (availabilityRes) {
        const allOverrides = Array.isArray(availabilityRes) ? availabilityRes : (availabilityRes.data || availabilityRes.overrides || [])
        // Filter for pending status
        const pendingOverrides = allOverrides.filter((o: any) => o.status === 'pending')
        setStats(prev => ({
          ...prev,
          pendingAvailability: pendingOverrides.length
        }))
      }

      // Handle custody schedule data
      let todayCustody: string[] = []
      let nextTransition: { date: string; kids: string[] } | undefined

      if (scheduleRes && scheduleRes.schedule && scheduleRes.schedule.length > 0) {
        // Get today's schedule
        const todaySchedule = scheduleRes.schedule.find((s: any) => s.date === todayStr)
        if (todaySchedule && todaySchedule.kids_present) {
          // Map usernames to display names
          todayCustody = todaySchedule.kids_present.map((username: string) => {
            const child = (childrenRes?.children || childrenRes?.data || []).find(
              (c: any) => c.username === username
            )
            return child?.display_name || username
          })
        }

        // Find next transition (day where custody changes from today)
        const todayPresence = todaySchedule?.presence || {}
        for (let i = 1; i < scheduleRes.schedule.length; i++) {
          const futureDay = scheduleRes.schedule[i]
          const futurePresence = futureDay.presence || {}

          // Check if any kid's presence status changes
          const hasChange = Object.keys(todayPresence).some(
            (kid) => todayPresence[kid] !== futurePresence[kid]
          )

          if (hasChange) {
            // Found a transition day
            const transitionDate = new Date(futureDay.date + 'T12:00:00')
            const kidsChanging = Object.keys(futurePresence).filter(
              (kid) => todayPresence[kid] !== futurePresence[kid]
            )

            nextTransition = {
              date: transitionDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric'
              }),
              kids: kidsChanging.map((username: string) => {
                const child = (childrenRes?.children || childrenRes?.data || []).find(
                  (c: any) => c.username === username
                )
                return child?.display_name || username
              })
            }
            break
          }
        }
      }

      // Handle meals response - get tonight's dinner
      let tonightsDinner: string | undefined
      if (mealsRes && Array.isArray(mealsRes) && mealsRes.length > 0) {
        // API returns array of DayPlan objects with nested breakfast/lunch/dinner
        const todayPlan = mealsRes.find((p: any) => p.date === todayStr)
        if (todayPlan?.dinner?.meal_name) {
          tonightsDinner = todayPlan.dinner.meal_name
        }
      }

      // Update stats with custody data and meals
      setStats(prev => ({
        ...prev,
        todayCustody,
        nextTransition,
        tonightsDinner
      }))
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePriorityClick = (section: 'verify' | 'overdue' | 'requests' | 'events') => {
    // Navigate to appropriate URL route with query params for section
    let path: string
    switch (section) {
      case 'verify':
        path = '/dashboard/chores?section=verification'  // Navigate to verification filter
        break
      case 'overdue':
        path = '/dashboard/chores?section=overdue'  // Navigate to overdue filter
        break
      case 'requests':
        path = '/dashboard/requests'
        break
      case 'events':
        path = '/dashboard/calendar'  // Navigate to calendar view
        break
      default:
        path = '/dashboard/chores'
    }
    navigate(path)
  }

  const handleLogout = () => {
    sessionStorage.removeItem('oauth_token_processed')
    logout()
    navigate('/login')
  }

  const handleTileClick = (section: 'chores' | 'calendar' | 'school' | 'kids' | 'rewards' | 'analytics' | 'meals' | 'settings' | 'requests') => {
    // Map tile sections to URL routes
    const viewMap: Record<string, string> = {
      'chores': '/dashboard/chores',
      'calendar': '/dashboard/calendar',
      'school': '/parent/school',
      'kids': '/dashboard/kids',  // Kids overview with all kids
      'rewards': '/dashboard/rewards',
      'analytics': '/dashboard/analytics',  // Dedicated family stats/analytics page
      'meals': '/dashboard/meals',
      'settings': '/dashboard/settings',
      'requests': '/dashboard/requests'
    }

    const path = viewMap[section] || '/dashboard/chores'
    navigate(path)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading dashboard...</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Family Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Welcome back, {user?.display_name || 'Parent'}
            </p>
          </div>

          {/* Dashboard Variant Toggle (legacy_view toggle) and Account Menu */}
          <div className="flex items-center gap-3">
            {legacyViewEnabled && (
              <div className="inline-flex bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => {
                    setDashboardVariant('A')
                  }}
                  className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 text-gray-600"
                >
                  <LayoutGrid className="w-4 h-4" />
                  Classic
                </button>
                <button
                  onClick={() => {
                    setDashboardVariant('B')
                  }}
                  className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 bg-white text-blue-600 shadow-sm"
                >
                  <Grid className="w-4 h-4" />
                  Tiles
                </button>
              </div>
            )}

            {hasPermission('system_settings') && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/settings')}
                aria-label="Settings"
                title="Settings"
                className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <SettingsIcon className="w-5 h-5" />
              </button>
            )}

            <AccountMenu />
          </div>
        </div>
      </div>

      <PriorityBar
        needsVerification={stats.needsVerification}
        overdue={stats.overdue}
        kidRequests={stats.kidRequests}
        todayEvents={stats.todayEvents}
        onSectionClick={handlePriorityClick}
      />

      <DashboardTiles
        onTileClick={handleTileClick}
        showMealPlanning={isFeatureEnabled('meal_planning') && hasPermission('manage_meals')}
        hiddenTiles={hiddenTiles}
        stats={{
          activeChores: stats.activeChores,
          upcomingEvents: stats.upcomingEvents,
          activeKids: stats.activeKids,
          pendingRewards: stats.pendingRewards,
          needsVerification: stats.needsVerification,
          overdue: stats.overdue,
          completedToday: stats.completedToday,
          weeklyCompletionRate: stats.weeklyCompletionRate,
          nextEvent: stats.nextEvent,
          topKid: stats.topKid,
          totalFamilyPoints: stats.totalFamilyPoints,
          tonightsDinner: stats.tonightsDinner,
          kidRequests: stats.kidRequests,
          pendingAvailability: stats.pendingAvailability
        }}
      />

      {/* Kids Overview */}
      {kidsOverview.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Kids Overview</h3>
            <button
              onClick={() => navigate('/dashboard/analytics')}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              View Stats <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {kidsOverview.map((kid) => (
              <button
                key={kid.id}
                onClick={() => navigate('/dashboard/kids')}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: kid.color_theme }}
                >
                  {kid.display_name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 text-sm truncate">{kid.display_name}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Lv {kid.level}</span>
                    {kid.streak_days > 0 && (
                      <span className="flex items-center gap-0.5">
                        <Flame className="w-3 h-3 text-orange-500" />
                        {kid.streak_days}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-bold text-sm" style={{ color: kid.color_theme }}>
                    {kid.available_points}
                  </div>
                  <div className="text-xs text-gray-400 flex items-center gap-0.5 justify-end">
                    <Star className="w-3 h-3" /> pts
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
